"use client";

import * as React from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { UI } from "react-day-picker";
import type { DayButtonProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Brand-aligned calendar using the warm cream/olive design system.
 *
 * Per-day styling (today / selected / disabled / outside) is NOT done via
 * react-day-picker's DayFlag modifier classNames — composing them as
 * separate CSS strings that all apply together to the same element let two
 * conflicting rules (the plain "today" tint and the "selected" solid fill)
 * both target the same day (today IS the initially-selected date on most
 * pickers here) with no way to guarantee which wins: `aria-selected:*`
 * beats a plain class on SPECIFICITY, but Tailwind's arbitrary-value
 * opacity-modifier rules (`bg-[var(--x)]/15`) get grouped with their
 * non-opacity sibling under a shared base selector in the generated CSS —
 * one of those interactions rendered today's cell as a filled block with
 * no visible date at all. CustomDayButton below computes ONE definitive
 * state per day in plain JS (disabled > outside > selected > today >
 * default) and picks a single classname branch — no CSS cascade order to
 * reason about, so this class of bug isn't reachable at all.
 *
 * Two other non-default props worth keeping:
 *  - navLayout="around" so prev/caption/next sit on one row, with the
 *    prev/next buttons absolute-positioned over the caption row (the
 *    caption itself is the only thing in normal flow, the chevron
 *    buttons floats over it at the left/right edges of UI.Month).
 *  - components.Dropdown overrides react-day-picker's native <select>
 *    with shadcn's Radix-based Select so the month/year pickers match
 *    the rest of the UI (no OS-native dropdown chrome).
 */
const calendarClassNames: Partial<Record<(typeof UI)[keyof typeof UI], string>> = {
  [UI.Root]: "p-3",
  [UI.Months]: "flex flex-col sm:flex-row gap-4",
  // Relative so PreviousMonthButton / NextMonthButton can sit absolute
  // at top-left and top-right while the caption stays centered.
  [UI.Month]: "relative space-y-3",
  [UI.MonthCaption]:
    "flex items-center justify-center h-9 gap-1.5 px-10 text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]",
  // navLayout="around" doesn't render a Nav element, so this class is
  // a no-op; kept defined to keep the typed map exhaustive.
  [UI.Nav]: "contents",
  [UI.PreviousMonthButton]:
    "absolute left-1 top-0 z-10 size-8 shrink-0 rounded-full inline-flex items-center justify-center text-[var(--fx-ink-700)] hover:bg-[var(--fx-s-overlay-hover)] hover:text-[var(--fx-ink-900)] disabled:opacity-40 disabled:hover:bg-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-500)] focus-visible:ring-offset-1 dark:text-[var(--dk-text)] dark:hover:bg-[var(--fx-s-overlay-hover)]",
  [UI.NextMonthButton]:
    "absolute right-1 top-0 z-10 size-8 shrink-0 rounded-full inline-flex items-center justify-center text-[var(--fx-ink-700)] hover:bg-[var(--fx-s-overlay-hover)] hover:text-[var(--fx-ink-900)] disabled:opacity-40 disabled:hover:bg-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-500)] focus-visible:ring-offset-1 dark:text-[var(--dk-text)] dark:hover:bg-[var(--fx-s-overlay-hover)]",
  [UI.CaptionLabel]:
    "text-sm font-semibold capitalize text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]",
  [UI.Dropdowns]: "flex items-center justify-center gap-1.5",
  // DropdownRoot wraps the shadcn Select below; remove default chrome.
  [UI.DropdownRoot]: "relative inline-flex items-center",
  [UI.Dropdown]: "",
  [UI.MonthsDropdown]: "",
  [UI.YearsDropdown]: "",
  [UI.Weekdays]: "flex",
  [UI.Weekday]:
    "w-9 font-medium text-[10px] uppercase tracking-[0.12em] text-[var(--fx-ink-500)] flex items-center justify-center dark:text-[var(--dk-text-mute)]",
  [UI.MonthGrid]: "w-full border-collapse mt-1",
  [UI.Weeks]: "flex flex-col gap-1",
  [UI.Week]: "flex w-full gap-0.5",
  [UI.Day]:
    "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
};

/**
 * One definitive visual state per day — see the file-level comment for why
 * this replaced DayFlag modifier classNames. Order is the precedence: a
 * disabled day never looks selected even if it technically is one; outside
 * (prev/next month) is visually de-emphasized regardless of other flags;
 * selected (the exact chosen date) is the strongest signal and wins over
 * merely being today; today is the fallback highlight when nothing else
 * applies.
 */
const DAY_BUTTON_BASE =
  "size-9 p-0 font-medium text-[13px] inline-flex items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-500)] focus-visible:ring-offset-1";

const DAY_STATE_CLASSNAMES = {
  disabled: "text-[var(--fx-ink-300)] opacity-50 dark:text-[var(--dk-text-mute)]",
  outside: "text-[var(--fx-ink-300)] dark:text-[var(--dk-text-mute)]",
  selected:
    // Hover is pinned to the resting fill on purpose: a selected day must not
    // react to hover. That is a no-op hover, not a neutral surface hover, so
    // the interaction layer does not apply here.
    // eslint-disable-next-line no-restricted-syntax
    "font-bold bg-[var(--fx-ink-900)] text-[var(--fx-paper)] hover:bg-[var(--fx-ink-900)] dark:bg-[var(--dk-text)] dark:text-[var(--dk-bg)]",
  today:
    "font-bold bg-[var(--fx-accent-100)] text-[var(--fx-accent-700)] hover:bg-[var(--fx-accent-100)] dark:bg-[var(--fx-accent-700)] dark:text-[var(--fx-accent-100)]",
  default:
    "text-[var(--fx-ink-900)] hover:bg-[var(--fx-s-overlay-hover)] dark:text-[var(--dk-text)] dark:hover:bg-[var(--fx-s-overlay-hover)]",
} as const;

function dayState(modifiers: DayButtonProps["modifiers"]): keyof typeof DAY_STATE_CLASSNAMES {
  if (modifiers.disabled) return "disabled";
  if (modifiers.outside) return "outside";
  if (modifiers.selected) return "selected";
  if (modifiers.today) return "today";
  return "default";
}

function CustomDayButton({ day, modifiers, className, ...buttonProps }: DayButtonProps) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      className={cn(DAY_BUTTON_BASE, DAY_STATE_CLASSNAMES[dayState(modifiers)], className)}
      {...buttonProps}
    />
  );
}

/**
 * Replace react-day-picker's native <select> dropdown with a Radix-based
 * shadcn Select so the month + year chips match the rest of the design
 * system. react-day-picker calls into this with a synthetic onChange
 * shaped like a real <select> ChangeEvent — we build one when the Radix
 * onValueChange fires so the v9 internals (which read e.target.value)
 * keep working unchanged.
 */
type DropdownComponent = NonNullable<
  React.ComponentProps<typeof DayPicker>["components"]
>["Dropdown"];

const ShadcnDropdown: DropdownComponent = (props) => {
  const { options, value, onChange, disabled, "aria-label": ariaLabel } = props;
  const stringValue = value != null ? String(value) : undefined;
  const selected = options?.find(
    (opt) => String(opt.value) === stringValue,
  );
  const handleValueChange = (next: string): void => {
    if (!onChange) return;
    onChange({
      target: { value: next },
      currentTarget: { value: next },
    } as unknown as React.ChangeEvent<HTMLSelectElement>);
  };
  return (
    <Select
      value={stringValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger
        aria-label={ariaLabel}
        className="h-7 w-auto gap-1 rounded-md border border-[var(--fx-ink-100)] bg-[var(--fx-paper)] px-2 py-0 text-sm font-medium text-[var(--fx-ink-900)] hover:border-[var(--fx-ink-200)] focus:ring-2 focus:ring-[var(--fx-accent-500)] focus:ring-offset-1 dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface)] dark:text-[var(--dk-text)]"
      >
        <SelectValue>{selected?.label}</SelectValue>
      </SelectTrigger>
      {/*
        z-[110] sits above DatePickerField's PopoverContent (z-[100]).
        Both render via portal at document body so z-index is the only
        knob — without this, the Select menu opens behind the calendar.
      */}
      <SelectContent className="z-[110] max-h-72">
        {options?.map((opt) => (
          <SelectItem
            key={opt.value}
            value={String(opt.value)}
            disabled={opt.disabled}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      navLayout="around"
      // No card chrome (bg/border/shadow/radius) here — the only consumer
      // (DatePickerField) always renders this inside a shadcn PopoverContent,
      // which already provides that (white bg, rounded-md, border, shadow).
      // A second nested card here just doubled up mismatched radii/colors.
      className={cn(
        "dark:text-[var(--dk-text)]",
        className,
      )}
      classNames={{ ...calendarClassNames, ...classNames }}
      components={{
        Chevron: ({ orientation, ...rest }) => {
          if (orientation === "left") {
            return <ChevronLeft className="size-4" {...rest} />;
          }
          if (orientation === "right") {
            return <ChevronRight className="size-4" {...rest} />;
          }
          if (orientation === "down") {
            return (
              <ChevronDown
                className="size-3.5 opacity-60"
                {...rest}
              />
            );
          }
          return <span aria-hidden className="hidden" />;
        },
        Dropdown: ShadcnDropdown,
        DayButton: CustomDayButton,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
